import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Droplets, Pipette, ShowerHead, Sparkles } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  {
    icon: Droplets,
    title: "Pitkä matka putkistossa",
    text: "Monissa kodeissa vesi kulkee pitkän matkan putkistoissa – usein vuosikymmeniä vanhojen putkien läpi – ennen kuin se päätyy suihkuun tai hanaan.",
  },
  {
    icon: Pipette,
    title: "Mukana ruostetta ja klooria",
    text: "Matkan varrella veteen voi päätyä ruostetta, klooria ja muita näkymättömiä hiukkasia, jotka voivat vaikuttaa ihon ja hiusten hyvinvointiin.",
  },
  {
    icon: ShowerHead,
    title: "Suoraan iholle ja hiuksille",
    text: "Joka suihkussa nämä epäpuhtaudet koskettavat ihoasi ja hiuksiasi – ja voivat kuivattaa ja ärsyttää niitä päivästä toiseen.",
  },
];

export default function ProblemStory() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-problem-step]").forEach((el, i) => {
        gsap.from(el, {
          y: 70,
          opacity: 0,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 82%" },
        });
      });
      gsap.from("[data-problem-line]", {
        scaleY: 0,
        transformOrigin: "top",
        ease: "none",
        scrollTrigger: {
          trigger: "[data-problem-steps]",
          start: "top 75%",
          end: "bottom 60%",
          scrub: 0.6,
        },
      });
      gsap.from("[data-problem-solution]", {
        y: 50,
        opacity: 0,
        scale: 0.96,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: "[data-problem-solution]", start: "top 85%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} data-testid="problem-section" className="relative py-24 md:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="eyebrow text-water-deep/80">Miksi veden laadulla on väliä?</p>
          <h2 className="h-section font-heading mt-5 font-semibold text-foreground" lang="fi">
            Tiesitkö, mitä suihkuvesi kuljettaa mukanaan?
          </h2>
        </div>

        <div data-problem-steps className="relative mt-16 md:mt-24 grid gap-14 md:gap-20 md:grid-cols-3">
          <span
            data-problem-line
            className="absolute left-6 top-2 hidden h-[calc(100%-1rem)] w-px bg-water md:left-0 md:top-auto md:h-px md:w-full md:bg-gradient-to-r md:from-water md:via-water md:to-transparent md:block"
            aria-hidden="true"
          />
          {STEPS.map((s, i) => (
            <div key={s.title} data-problem-step className="relative pl-16 md:pl-0 md:pt-14">
              <span className="absolute left-0 top-0 md:left-0 md:-top-0 flex h-12 w-12 items-center justify-center rounded-2xl bg-water text-water-deep shadow-sm">
                <s.icon size={22} strokeWidth={1.8} />
              </span>
              <p className="text-xs font-extrabold tracking-widest text-muted-foreground">0{i + 1}</p>
              <h3 className="h-sub font-heading mt-2 font-semibold text-foreground">{s.title}</h3>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>

        <div
          data-problem-solution
          className="mt-20 md:mt-28 rounded-[2rem] bg-primary p-10 md:p-16 text-primary-foreground shadow-2xl shadow-primary/25"
        >
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:gap-10">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15">
              <Sparkles size={26} strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="h-sub font-heading font-semibold" lang="fi">
                JINSU puhdistaa veden juuri ennen käyttöä.
              </h3>
              <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-primary-foreground/85">
                Siellä, missä sillä on eniten merkitystä. Suodatin kiinnittyy suihkuletkun ja hanan
                väliin ja poistaa ruosteen, kloorin ja epäpuhtaudet ennen kuin vesi koskettaa ihoasi.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
